import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ConfigProvider, theme, App as AntdApp } from "antd";
import LogRocket from "logrocket";
import { useTranslation } from "react-i18next";

// Ant Design Locales
import zhTW from "antd/locale/zh_TW";
import enUS from "antd/locale/en_US";
import esES from "antd/locale/es_ES";

import { isLoggedIn } from "./shared/utils/Auth";
import { AuthProvider } from "./shared/contexts";
import { ProtectedRoute } from "./shared/components/layout/ProtectedRoute";
import AdminLayout from "./shared/components/layout/AdminLayout";
import { RequireFeatureFlag } from "./shared/components/layout";
import ManagerLayout from "./shared/components/layout/ManagerLayout";
import AuthenticatedLayout from "./shared/components/layout/AuthenticatedLayout";
import Home from "./pages/Home";
import RestaurantSelection from "./pages/RestaurantSelection";
import AdminDashboard from "./pages/AdminDashboard";
import ManagerDashboard from "./pages/ManagerDashboard";
import MemberManagementPage from "./pages/MemberManagementPage";
import AnnouncementEditorPage from "./pages/AnnouncementEditorPage";
import CloverCallback from "./features/callback/CloverCallback";
import ControlStation from "./features/orders/ControlStation";
import DoneOrderView from "./features/orders/DoneOrderView";
import EditorPage from "./pages/EditorPage";
import InventoryPage from "./pages/InventoryPage";
import ItemModificationSchemaEditorPage from "./pages/ItemModificationSchemaEditorPage";
import ComboAddonsPage from "./pages/ComboAddonsPage";
import CampaignEditorPage from "./pages/CampaignEditorPage";
import CampaignsPage from "./pages/CampaignsPage";
import OrderLookupSearch from "./features/orders/OrderLookupSearch";
import OrderLookupResults from "./features/orders/OrderLookupResults";
import StatsPage from "./pages/StatsPage";
import ReportsPage from "./pages/ReportsPage";
import ShippingManagementPage from "./pages/ShippingManagementPage";
import QrCodeManagementPage from "./pages/QrCodeManagementPage";
import RestaurantFeatureFlagPage from "./pages/RestaurantFeatureFlagPage";
import SquareIntegrationPage from "./pages/SquareIntegrationPage";
import PointGiftCardPage from "./pages/PointGiftCardPage";
import WorkstationSchemaPage from "./pages/WorkstationSchemaPage";
import ItemSchemaGroupPage from "./pages/ItemSchemaGroupPage";
import RestaurantPublicInfoPage from "./pages/RestaurantPublicInfoPage";
import PointsConfigPage from "./pages/PointsConfigPage";
import WaitConfigPage from "./pages/WaitConfigPage";
import LargeVolumeDeliveryPage from "./pages/LargeVolumeDeliveryPage";
import LargeVolumePickupConfigPage from "./pages/LargeVolumePickupConfigPage";
import PromoCodesPage from "./pages/PromoCodesPage";
import PrepaidCardsPage from "./pages/PrepaidCardsPage";
import PrepaidCardStatsPage from "./pages/PrepaidCardStatsPage";
import UserManagementPage from "./pages/UserManagementPage";
import TridConverterPage from "./pages/TridConverterPage";
import WorkStation from "./features/workstation/WorkStation";
import Login from "./pages/Login";
import { AcceptInvitePage } from "./features/users/AcceptInvitePage";
import { scanPrinters } from "./shared/utils/Print";
import { PrinterManagementPage } from "./pages/PrinterManagementPage";

import "./styles/theme-global.css";
import appTheme from "./config/theme";

if (import.meta.env.PROD && import.meta.env.VITE_LOGROCKET_ID) {
  LogRocket.init(import.meta.env.VITE_LOGROCKET_ID);
}

// Modern React Router v7 PrivateRoute component
interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const loggedIn = isLoggedIn();
  const location = useLocation();

  if (!loggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Get Ant Design locale based on i18n language
const getAntdLocale = (language: string) => {
  switch (language) {
    case "zh":
      return zhTW;
    case "es":
      return esES;
    case "en":
    default:
      return enUS;
  }
};

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const [antdLocale, setAntdLocale] = useState(getAntdLocale(i18n.language));

  // Update Ant Design locale when language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setAntdLocale(getAntdLocale(lng));
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);
  React.useEffect(() => {
    // On page load, instruct the print agent to scan for printers.
    // There's built-in throttling in the agent to handle refreshes in quick succession.
    scanPrinters();
  }, []);

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        algorithm: theme.defaultAlgorithm,
        cssVar: { prefix: "ant" },
        token: {
          // Colors
          colorPrimary: appTheme.colors.primary.main,
          colorSuccess: appTheme.colors.success,
          colorWarning: appTheme.colors.warning,
          colorError: appTheme.colors.error,
          colorInfo: appTheme.colors.info,

          // Typography
          fontFamily: appTheme.typography.fontFamily.base,
          fontSize: parseInt(appTheme.typography.fontSize.sm),
          fontSizeHeading1: parseInt(appTheme.typography.fontSize["4xl"]),
          fontSizeHeading2: parseInt(appTheme.typography.fontSize["3xl"]),
          fontSizeHeading3: parseInt(appTheme.typography.fontSize["2xl"]),
          fontSizeHeading4: parseInt(appTheme.typography.fontSize.xl),
          fontSizeHeading5: parseInt(appTheme.typography.fontSize.lg),

          // Spacing
          marginXXS: parseInt(appTheme.spacing.xs),
          marginXS: parseInt(appTheme.spacing.sm),
          marginSM: parseInt(appTheme.spacing.sm),
          margin: parseInt(appTheme.spacing.md),
          marginMD: parseInt(appTheme.spacing.md),
          marginLG: parseInt(appTheme.spacing.lg),
          marginXL: parseInt(appTheme.spacing.xl),
          marginXXL: parseInt(appTheme.spacing.xxl),

          // Border
          borderRadius: parseInt(appTheme.borderRadius.md),
          borderRadiusLG: parseInt(appTheme.borderRadius.lg),
          borderRadiusSM: parseInt(appTheme.borderRadius.sm),

          // Control sizing
          controlHeight: 44,
          controlHeightLG: 48,
          controlHeightSM: 32,

          // Responsive breakpoints (AntD uses these internally)
          screenXS: parseInt(appTheme.breakpoints.xs),
          screenSM: parseInt(appTheme.breakpoints.sm),
          screenMD: parseInt(appTheme.breakpoints.md),
          screenLG: parseInt(appTheme.breakpoints.lg),
          screenXL: parseInt(appTheme.breakpoints.xl),
          screenXXL: parseInt(appTheme.breakpoints.xxl),
        },
        components: {
          Layout: {
            headerBg: appTheme.colors.primary.dark,
            headerColor: appTheme.colors.text.inverse,
            headerHeight: parseInt(appTheme.layout.header.height),
            headerPadding: appTheme.layout.header.padding,
            bodyBg: appTheme.colors.background.secondary,
          },
          Button: {
            fontSizeLG: parseInt(appTheme.typography.fontSize.base),
            controlHeight: 44,
            controlHeightLG: 48,
            paddingContentHorizontal: parseInt(appTheme.spacing.lg),
          },
          Card: {
            borderRadiusLG: parseInt(appTheme.borderRadius.xl),
            boxShadowTertiary: appTheme.shadows.base,
            paddingLG: parseInt(appTheme.spacing.xl),
          },
          Input: {
            controlHeight: 44,
            paddingBlock: parseInt(appTheme.spacing.sm),
            paddingInline: parseInt(appTheme.spacing.md),
          },
          Select: {
            controlHeight: 44,
          },
          Typography: {
            titleMarginTop: 0,
            titleMarginBottom: "0.5em",
          },
          Space: {
            // Configure default spacing for Space component
          },
        },
      }}
    >
      <AntdApp>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/clover/callback" element={<CloverCallback />} />

              {/* Admin Section - Nested Routes with Single Protection Point */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="editor" element={<EditorPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route
                  path="item-modification-schema"
                  element={<ItemModificationSchemaEditorPage />}
                />
                <Route path="combo-addons" element={<ComboAddonsPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route
                  path="campaign-editor"
                  element={
                    <RequireFeatureFlag flagName="EnableCampaignCreationUI">
                      <CampaignEditorPage />
                    </RequireFeatureFlag>
                  }
                />
                <Route path="members" element={<MemberManagementPage />} />
                <Route path="shipping" element={<ShippingManagementPage />} />
                <Route path="stats" element={<StatsPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="qr-code" element={<QrCodeManagementPage />} />
                <Route path="announcements" element={<AnnouncementEditorPage />} />
                <Route path="flags" element={<RestaurantFeatureFlagPage />} />
                <Route path="feature-flags" element={<RestaurantFeatureFlagPage />} />
                <Route path="square-integrations" element={<SquareIntegrationPage />} />
                <Route path="point-gift-cards" element={<PointGiftCardPage />} />
                <Route path="workstation-schemas" element={<WorkstationSchemaPage />} />
                <Route path="item-schema-groups" element={<ItemSchemaGroupPage />} />
                <Route path="restaurant-public-info" element={<RestaurantPublicInfoPage />} />
                <Route path="points-config" element={<PointsConfigPage />} />
                <Route path="wait-config" element={<WaitConfigPage />} />
                <Route path="large-volume-delivery" element={<LargeVolumeDeliveryPage />} />
                <Route
                  path="large-volume-pickup-config"
                  element={<LargeVolumePickupConfigPage />}
                />
                <Route path="promo-codes" element={<PromoCodesPage />} />
                <Route
                  path="prepaid-cards"
                  element={
                    <RequireFeatureFlag flagName="PrepaidCard">
                      <PrepaidCardsPage />
                    </RequireFeatureFlag>
                  }
                />
                <Route
                  path="prepaid-card-stats"
                  element={
                    <RequireFeatureFlag flagName="PrepaidCard">
                      <PrepaidCardStatsPage />
                    </RequireFeatureFlag>
                  }
                />
                <Route path="users" element={<UserManagementPage />} />
              </Route>

              {/* Manager Section - Nested Routes with Limited Access */}
              <Route
                path="/manager"
                element={
                  <ProtectedRoute requireManagerAccess={true}>
                    <ManagerLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<ManagerDashboard />} />
                <Route path="stats" element={<StatsPage />} />
                <Route path="qr-code" element={<QrCodeManagementPage />} />
              </Route>

              {/* Authenticated Section - Nested Routes with Shared Layout */}
              <Route
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout />
                  </PrivateRoute>
                }
              >
                <Route path="/" element={<Home />} />
                <Route path="/printer-management" element={<PrinterManagementPage />} />
                <Route path="/master-control-station" element={<ControlStation />} />
                <Route path="/done-order-view" element={<DoneOrderView />} />
                <Route path="/order-lookup" element={<OrderLookupSearch />} />
                <Route path="/order-lookup/results" element={<OrderLookupResults />} />
              </Route>

              {/* WorkStation - Separate Route (uses dynamic title from state) */}
              <Route
                path="/work-station"
                element={
                  <PrivateRoute>
                    <WorkStation />
                  </PrivateRoute>
                }
              />

              <Route path="/select-restaurant" element={<RestaurantSelection />} />
              <Route path="/invite/:tokenId" element={<AcceptInvitePage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/trid" element={<TridConverterPage />} />
              <Route path="/healthz" element={<div>OK</div>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
